import { useEffect, useRef } from 'react';

/**
 * Cloudflare Turnstile widget (registration captcha, D25).
 *
 * Loads the Turnstile script once per page, renders an explicit widget into a
 * div and hands the challenge token to `onToken` (null again when it expires
 * or errors, so the form can disable its submit button). Bump `resetKey` to
 * request a fresh challenge after the server rejected a token.
 *
 * Only this component knows about `window.turnstile`; the rest of the app just
 * sees a token string. The production CSP (install/nginx/*.conf.template)
 * allows challenges.cloudflare.com in script-src and frame-src for this.
 */

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: (code?: string) => void;
  'timeout-callback'?: () => void;
}

interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string | undefined;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise: Promise<TurnstileApi> | null = null;

/** Resolve to the Turnstile API, injecting the script tag on first use. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const fail = (why: string) => {
      scriptPromise = null;
      reject(new Error(why));
    };
    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else fail('Turnstile script loaded without exposing its API');
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', settle, { once: true });
      existing.addEventListener('error', () => fail('Turnstile script failed to load'), {
        once: true,
      });
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', settle, { once: true });
    s.addEventListener('error', () => fail('Turnstile script failed to load'), { once: true });
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface TurnstileProps {
  siteKey: string;
  /** Called with the challenge token, or null when it expired / errored / was reset. */
  onToken: (token: string | null) => void;
  /** Called when the widget itself could not be loaded or rendered. */
  onLoadError?: (err: unknown) => void;
  /** Change the value to reset the widget and get a fresh challenge. */
  resetKey?: number;
  action?: string;
  className?: string;
}

export function Turnstile({
  siteKey,
  onToken,
  onLoadError,
  resetKey = 0,
  action = 'register',
  className = '',
}: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const apiRef = useRef<TurnstileApi | null>(null);
  // Latest callbacks without re-rendering the widget when the parent re-renders.
  const onTokenRef = useRef(onToken);
  const onLoadErrorRef = useRef(onLoadError);
  onTokenRef.current = onToken;
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    const el = container.current;
    if (!el) return;
    loadTurnstile()
      .then((api) => {
        if (cancelled || !container.current) return;
        apiRef.current = api;
        const id = api.render(container.current, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          size: 'flexible',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
          'timeout-callback': () => onTokenRef.current(null),
        });
        widgetId.current = id ?? null;
        if (!id) onLoadErrorRef.current?.(new Error('Turnstile did not render'));
      })
      .catch((err: unknown) => {
        if (!cancelled) onLoadErrorRef.current?.(err);
      });
    return () => {
      cancelled = true;
      if (widgetId.current && apiRef.current) {
        try {
          apiRef.current.remove(widgetId.current);
        } catch {
          // widget already gone
        }
      }
      widgetId.current = null;
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (resetKey === 0) return;
    if (widgetId.current && apiRef.current) {
      onTokenRef.current(null);
      apiRef.current.reset(widgetId.current);
    }
  }, [resetKey]);

  return <div ref={container} className={className} data-testid="captcha" />;
}
